import { useState, useEffect } from 'react';
import { FileText, Edit2, Trash2, BookOpen } from 'lucide-react';
import { api } from '../lib/api';
import BottomNav from '../components/BottomNav';

interface Note {
  id: string;
  card_id: string;
  content: string;
  card_title: string;
  book_title: string;
  created_at: string;
  updated_at: string;
}

export default function Notes() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  useEffect(() => {
    loadNotes();
  }, []);

  const loadNotes = async () => {
    try {
      const response = await api.get('/notes');
      setNotes(response.data);
    } catch (error) {
      console.error('加载笔记失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (note: Note) => {
    setEditingId(note.id);
    setEditContent(note.content);
  };

  const handleSave = async (id: string) => {
    try {
      await api.put(`/notes/${id}`, { content: editContent });
      setNotes(notes.map(n => n.id === id ? { ...n, content: editContent, updated_at: new Date().toISOString() } : n));
      setEditingId(null);
    } catch (error) {
      alert('保存失败');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('确定要删除这条笔记吗?')) return;

    try {
      await api.delete(`/notes/${id}`);
      setNotes(notes.filter(n => n.id !== id));
    } catch (error) {
      alert('删除失败');
    }
  };

  return (
    <div className="min-h-screen bg-[#1a2e3e] pb-24">
      {/* 头部 */}
      <div className="p-6">
        <h1 className="text-3xl font-bold text-white mb-2">我的笔记</h1>
        <p className="text-white/60">共 {notes.length} 条笔记</p>
      </div>

      {/* 笔记列表 */}
      <div className="px-6 space-y-4">
        {loading ? (
          <div className="text-center py-12 text-white/60">
            加载中...
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60">还没有添加任何笔记</p>
          </div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="glass rounded-2xl p-5">
              {/* 头部 */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <BookOpen className="w-4 h-4 text-[#22a9d9]" />
                    <span className="text-white/60 text-sm">{note.book_title}</span>
                  </div>
                  <h3 className="text-white font-medium">
                    {note.card_title}
                  </h3>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(note)}
                    className="text-[#22a9d9] hover:text-[#22a9d9]/80 transition-colors"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(note.id)}
                    className="text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* 笔记内容 */}
              {editingId === note.id ? (
                <div className="space-y-3">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full h-32 bg-white/5 rounded-xl p-3 text-white placeholder-white/40 outline-none resize-none"
                    placeholder="编辑笔记..."
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleSave(note.id)}
                      className="flex-1 h-10 rounded-xl bg-[#22a9d9] text-white font-medium hover:bg-[#22a9d9]/80 transition-colors"
                    >
                      保存
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="flex-1 h-10 rounded-xl bg-white/10 text-white font-medium hover:bg-white/20 transition-colors"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-white/70 text-sm leading-relaxed whitespace-pre-wrap">
                    {note.content}
                  </p>
                  <div className="mt-4 text-white/40 text-xs">
                    {note.updated_at !== note.created_at ? '编辑于' : '创建于'} {new Date(note.updated_at).toLocaleString()}
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
}
